/**
 * Chat Panel Component
 *
 * Main chat panel that combines header, messages, and input.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Slide,
  Fade,
  CircularProgress,
  Typography,
  Chip,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import ChatHeader from './chat-header';
import ChatMessage from './chat-message';
import ChatInput from './chat-input';
import ChatWelcome from './chat-welcome';
import ThinkingTimeline from './thinking-timeline';
import useAIChatStore from '../../../stores/ai-chat-store';
import { useAIChat } from '../../../hooks/use-ai-chat';
import { CHAT_PANEL_DIMENSIONS } from '../ai-chat.types';
import { dc } from '../design-tokens';

interface ChatPanelProps {
  embedded?: boolean;
  className?: string;
  onClearHistory?: () => void;
  onRestartConversation?: () => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  embedded = false,
  className,
  onClearHistory,
  onRestartConversation,
}) => {
  const { classes, cx } = useStyles();
  const { t, i18n } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const {
    isOpen,
    isFullscreen,
    setOpen,
    context: storeContext,
    queuedUserMessage,
    clearQueuedUserMessage,
  } = useAIChatStore((state) => ({
    isOpen: state.isOpen,
    isFullscreen: state.isFullscreen,
    setOpen: state.setOpen,
    context: state.context,
    queuedUserMessage: state.queuedUserMessage,
    clearQueuedUserMessage: state.clearQueuedUserMessage,
  }));

  // Always include locale from i18n in the context
  const context = React.useMemo(
    () => ({ ...storeContext, locale: storeContext.locale || i18n.language }),
    [storeContext, i18n.language]
  );

  const {
    messages,
    isLoading,
    isSending,
    isStreaming,
    streamingContent,
    latestSuggestions,
    liveThinking,
    error,
    send,
    clearHistory,
    cancelStreaming,
  } = useAIChat();

  // Merge streaming content into the last message for display
  const displayMessages = React.useMemo(() => {
    // Filter out internal messages that shouldn't be displayed:
    // Only filter out 'tool' role messages (internal AI-to-tool communication)
    // Keep all user and assistant messages
    const filteredMessages = messages.filter((msg) => {
      // Only hide tool role messages - these are internal to the AI conversation
      if (msg.role === 'tool') return false;
      return true;
    });

    // If not streaming, return filtered messages
    if (!isStreaming) return filteredMessages;

    // Find the last assistant message and update its content with streaming content
    const lastIndex = filteredMessages.length - 1;
    if (lastIndex < 0) return filteredMessages;

    const lastMessage = filteredMessages[lastIndex];
    // If the last message is an assistant message (the one we're streaming)
    if (lastMessage.role === 'assistant') {
      return [
        ...filteredMessages.slice(0, lastIndex),
        { ...lastMessage, content: streamingContent || lastMessage.content, isStreaming: true },
      ];
    }

    return filteredMessages;
  }, [messages, isStreaming, streamingContent]);

  // Proposal actions (accept / modify / ...) from the latest assistant
  // message feed the quick-reply chips above the input — internal execution
  // metadata (phases, planner/critic steps, quality scores) is deliberately
  // NOT rendered; it reads as debug output to end users.
  const latestProposalActions = React.useMemo(() => {
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      const msg = displayMessages[i];
      if (msg.role === 'assistant' && msg.executionMeta) {
        return msg.executionMeta.proposal?.actions || [];
      }
    }
    return [];
  }, [displayMessages]);

  // Live status while a message is processed. Driven by the REAL thinking
  // events from the orchestrator (plan → tool_call → observe → decide);
  // the generic "preparing" line only bridges the gap until the first event.
  // Während der ganzen Wartezeit sichtbar — ab „Nachricht verstehen“, auch bevor das
  // erste Server-Ereignis da ist.
  const showThinkingTimeline = isStreaming;
  const timeline = (<ThinkingTimeline steps={liveThinking} isActive={isStreaming} answering={Boolean(streamingContent)} />);

  const actionMessageMap: Record<string, string> = {
    accept: 'accept',
    modify: 'modify',
    test: 'test',
    continue: 'continue',
  };
  const actionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      accept: t('proposalAccept', 'Accept'),
      modify: t('proposalModify', 'Modify'),
      test: t('proposalTest', 'Test'),
      continue: t('proposalContinue', 'Continue'),
    };
    return labels[action] || action;
  };

  // Scroll to bottom when new messages arrive or streaming content updates
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [displayMessages, streamingContent, liveThinking.length, scrollToBottom]);

  const handleSend = useCallback(
    (content: string) => {
      send(content, context);
    },
    [send, context]
  );

  // Messages queued by side panels (e.g. the profile data form after saving)
  // are sent as the user's next chat message. Only the embedded instance
  // consumes the queue — the floating panel instance stays passive.
  useEffect(() => {
    if (!embedded || !queuedUserMessage || isSending || isStreaming) return;
    const message = queuedUserMessage;
    clearQueuedUserMessage();
    send(message, context);
  }, [embedded, queuedUserMessage, isSending, isStreaming, clearQueuedUserMessage, send, context]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleClearHistory = useCallback(() => {
    if (onClearHistory) {
      onClearHistory();
    } else {
      clearHistory();
    }
  }, [clearHistory, onClearHistory]);

  const handleRestartConversation = useCallback(() => {
    if (onRestartConversation) {
      onRestartConversation();
    } else {
      clearHistory();
    }
  }, [clearHistory, onRestartConversation]);

  const handleSuggestedAction = useCallback(
    (message: string) => {
      send(message, context);
    },
    [send, context]
  );

  const handleRetry = useCallback(() => {
    if (messages.length > 0) {
      const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user');
      if (lastUserMessage) {
        send(lastUserMessage.content, context);
      }
    }
  }, [messages, send, context]);

  // For floating panel, don't render if not open
  if (!embedded && !isOpen) {
    return null;
  }

  const panelContent = (
    <Paper
      className={cx(
        classes.panel,
        embedded && 'embedded',
        isFullscreen && 'fullscreen',
        className
      )}
      elevation={embedded ? 0 : 8}
    >
      {/* Auf der Chat-Seite (embedded) ohne Kopfleiste — die Sidebar trägt die Aktionen */}
      {!embedded && (
        <ChatHeader
          onClose={handleClose}
          onClearHistory={handleClearHistory}
          onRestartConversation={handleRestartConversation}
          onNewChat={handleRestartConversation}
          showClose
          showMinimize
          showFullscreen
        />
      )}

      <Box ref={messagesContainerRef} className={classes.messagesContainer}>

        {isLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="textSecondary">
              {t('loadingConversation', 'Loading conversation...')}
            </Typography>
          </Box>
        ) : displayMessages.length === 0 ? (
          <ChatWelcome
            onSuggestedAction={handleSuggestedAction}
            onOpenSidePanel={
              embedded
                ? (panel) => {
                    const st = useAIChatStore.getState();
                    st.setSidePanel(panel);
                    st.setSetupModalOpen(true);
                  }
                : undefined
            }
          />
        ) : (
          <Box className={classes.messagesList}>
            {displayMessages.map((message, index) => (
              <React.Fragment key={message.id}>
                {/* Direkt über der entstehenden Antwort — oben im Verlauf war sie in längeren
                    Gesprächen weggescrollt. */}
                {showThinkingTimeline && message.isStreaming && timeline}
                <ChatMessage
                  message={message}
                  isLast={index === displayMessages.length - 1 && message.role === 'assistant'}
                  onRetry={index === displayMessages.length - 1 ? handleRetry : undefined}
                />
              </React.Fragment>
            ))}
            {showThinkingTimeline && !displayMessages.some((m) => m.isStreaming) && timeline}
            <div ref={messagesEndRef} />
          </Box>
        )}

        {error && (
          <Box className={classes.errorContainer}>
            <Typography variant="body2" color="error">
              {error instanceof Error ? error.message : t('errorOccurred', 'An error occurred')}
            </Typography>
          </Box>
        )}
      </Box>

      {!isStreaming && (latestSuggestions.length > 0 || latestProposalActions.length > 0) && (
        <Box className={classes.suggestionsBar}>
          {latestProposalActions.slice(0, 3).map((action) => (
            <Chip
              key={`proposal-${action}`}
              label={actionLabel(action)}
              size="small"
              clickable
              color="primary"
              variant="outlined"
              onClick={() => handleSend(actionMessageMap[action] || action)}
              className={classes.suggestionChip}
            />
          ))}
          {latestSuggestions.slice(0, 3).map((suggestion, idx) => (
            <Chip
              key={`inline-suggestion-${idx}`}
              label={suggestion}
              size="small"
              clickable
              onClick={() => handleSend(suggestion)}
              className={classes.suggestionChip}
            />
          ))}
        </Box>
      )}

      <ChatInput
        onSend={handleSend}
        onStop={cancelStreaming}
        disabled={isLoading}
        isSending={isSending}
        isStreaming={isStreaming}
        placeholder={t('typeYourMessage', 'Type your message...')}
      />
    </Paper>
  );

  // For embedded mode, just return the panel
  if (embedded) {
    return panelContent;
  }

  // For floating mode, wrap in Slide transition
  return (
    <Box className={cx(classes.floatingContainer, isFullscreen && 'fullscreen')}>
      <Fade in={isOpen}>
        <Box className={classes.backdrop} onClick={handleClose} />
      </Fade>
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        {panelContent}
      </Slide>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  floatingContainer: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1300,
    '&.fullscreen': {
      bottom: 0,
      right: 0,
      top: 0,
      left: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: -1,
    display: 'none',
    '.fullscreen &': {
      display: 'block',
    },
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    width: CHAT_PANEL_DIMENSIONS.defaultWidth,
    maxWidth: '100vw',
    height: 600,
    maxHeight: 'calc(100vh - 100px)',
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greys.lighter}`,
    boxShadow: `0 16px 40px ${theme.palette.greys.medium}33`,
    '&.embedded': {
      width: '100%',
      maxWidth: 'none',
      height: '100%',
      maxHeight: 'none',
      borderRadius: 0,
      backgroundColor: dc.surface,
      border: 'none',
    },
    '&.fullscreen': {
      width: '100%',
      maxWidth: CHAT_PANEL_DIMENSIONS.fullscreenMaxWidth,
      height: '90vh',
      maxHeight: '90vh',
      margin: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '100%',
      maxHeight: '100%',
      borderRadius: 0,
    },
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    background: dc.surface,
    scrollbarWidth: 'thin',
    scrollbarColor: '#C0C9E0 transparent',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#C9D2E8',
      borderRadius: 999,
      '&:hover': {
        backgroundColor: dc.faint,
      },
    },
  },
  workflowBanner: {
    position: 'relative',
    margin: theme.spacing(1, 2, 0, 2),
    padding: theme.spacing(1.25),
    borderRadius: 12,
    backgroundColor: `${theme.palette.background.paper}F2`,
    backdropFilter: 'blur(8px)',
    border: `1px solid ${theme.palette.greys.lighter}`,
    boxShadow: `0 6px 16px ${theme.palette.greys.light}55`,
    '&:hover $workflowToggleButton': {
      opacity: 1,
    },
    '&:focus-within $workflowToggleButton': {
      opacity: 1,
    },
  },
  workflowPhase: {
    display: 'block',
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  preparingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  suggestionsBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 3, 1, 3),
    maxWidth: 660,
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  suggestionChip: {
    maxWidth: '100%',
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 10,
    color: dc.ink,
    fontSize: '0.8rem',
    '&:hover': {
      borderColor: dc.primary,
      color: dc.primary,
      backgroundColor: dc.primaryHoverBg,
    },
  },
  workflowQuestion: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
  workflowActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
  },
  qualityCheckRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    borderRadius: 8,
    backgroundColor: theme.palette.greys.lightest,
    border: `1px solid ${theme.palette.greys.lighter}`,
  },
  workflowStatus: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  workflowHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.25),
  },
  workflowToggleButton: {
    padding: 2,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: theme.palette.background.paper,
    opacity: 0,
    transition: 'opacity 0.2s ease',
    '&:focus-visible': {
      opacity: 1,
    },
  },
  expandIconClosed: {
    transform: 'rotate(0deg)',
    transition: 'transform 0.2s ease',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
    transition: 'transform 0.2s ease',
  },
  workflowAccordion: {
    marginTop: theme.spacing(0.25),
    background: 'transparent',
    boxShadow: 'none',
    '&::before': {
      display: 'none',
    },
  },
  workflowAccordionDetails: {
    padding: theme.spacing(0.5, 0, 0, 0),
  },
  understoodList: {
    margin: theme.spacing(0.5, 0, 0.5, 2),
    padding: 0,
    color: theme.palette.text.primary,
    '& li': {
      marginBottom: theme.spacing(0.25),
      fontSize: '0.82rem',
      lineHeight: 1.45,
    },
  },
  workflowChip: {
    fontWeight: 600,
  },
  logHintChip: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  messagesList: {
    // v2: komfortable Lesebreite, mittig, ohne Lücke zum Header
    maxWidth: 660,
    margin: '0 auto',
    padding: theme.spacing(2.5, 3, 1, 3),
    boxSizing: 'border-box',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    height: '100%',
  },
  errorContainer: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
}));

export default ChatPanel;

